import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users as UsersIcon, ShieldPlus, UserPlus } from 'lucide-react'
import { useAuth } from '../store/auth'
import { getUsers, getRoles, assignRole, revokeRole, createUser } from '../api/auth'
import { Badge } from '../components/Badge'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { SearchInput } from '../components/SearchInput'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { User, Role } from '../types'

export function Users() {
  const fmt = useDateFmt()
  const { user: me } = useAuth()
  const businessId = me!.businessId
  const qc = useQueryClient()

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', businessId],
    queryFn: () => getUsers(businessId).then((r) => r.data),
  })

  const { data: roles = [] } = useQuery({
    queryKey: ['roles', businessId],
    queryFn: () => getRoles(businessId).then((r) => r.data),
  })

  const [search, setSearch] = useState('')
  const filtered = users.filter((u: User) =>
    [u.fullName, u.email].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const [showAddUser, setShowAddUser] = useState(false)
  const [newUser, setNewUser] = useState({ fullName: '', email: '', phone: '' })
  const [addError, setAddError] = useState('')

  const add = useMutation({
    mutationFn: (data: { fullName: string; email: string; phone: string }) =>
      createUser({ businessId, ...data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setShowAddUser(false)
      setNewUser({ fullName: '', email: '', phone: '' })
      setAddError('')
    },
    onError: (err: any) => {
      setAddError(err.response?.data?.error || 'Failed to create user')
    },
  })

  const handleAdd = () => {
    if (!newUser.fullName || !newUser.email || !newUser.phone) return
    setAddError('')
    add.mutate(newUser)
  }

  const [manageTarget, setManageTarget] = useState<User | null>(null)
  const [checkedRoles, setCheckedRoles] = useState<Set<string>>(new Set())
  const [manageError, setManageError] = useState('')
  const [isSavingRoles, setIsSavingRoles] = useState(false)

  const openManage = (u: User) => {
    const currentIds = new Set(
      u.roles.map((name) => roles.find((r: Role) => r.name === name)?.id).filter(Boolean) as string[]
    )
    setCheckedRoles(currentIds)
    setManageTarget(u)
    setManageError('')
  }

  const toggleRole = (roleId: string) => {
    setCheckedRoles((prev) => {
      const next = new Set(prev)
      next.has(roleId) ? next.delete(roleId) : next.add(roleId)
      return next
    })
  }

  const handleSaveRoles = async () => {
    if (!manageTarget) return
    const currentIds = new Set(
      manageTarget.roles.map((name) => roles.find((r: Role) => r.name === name)?.id).filter(Boolean) as string[]
    )
    const toAssign = [...checkedRoles].filter((id) => !currentIds.has(id))
    const toRevoke = [...currentIds].filter((id) => !checkedRoles.has(id))
    setIsSavingRoles(true)
    setManageError('')
    try {
      await Promise.all([
        ...toAssign.map((roleId) => assignRole(manageTarget.id, roleId)),
        ...toRevoke.map((roleId) => revokeRole(manageTarget.id, roleId)),
      ])
      qc.invalidateQueries({ queryKey: ['users'] })
      setManageTarget(null)
    } catch {
      setManageError('Failed to save role changes')
    } finally {
      setIsSavingRoles(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Users</h1>
          <p className="text-sm text-neutral-500 mt-0.5">All users in your organisation</p>
        </div>
        <div className="flex items-center gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search users" />
          <Button onClick={() => { setShowAddUser(true); setAddError('') }} icon={UserPlus}>
            Add user
          </Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-neutral-400">Loading</div>
        ) : users.length === 0 ? (
          <EmptyState icon={UsersIcon} title="No users yet" description="Users are created via the registration flow" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((u: User) => (
                <tr key={u.id}>
                  <td className="font-medium text-neutral-900">{u.fullName}</td>
                  <td className="text-neutral-500">{u.email}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length === 0
                        ? <span className="text-neutral-400 text-xs"></span>
                        : u.roles.map((r) => <Badge key={r} variant="default">{r}</Badge>)
                      }
                    </div>
                  </td>
                  <td>
                    <Badge variant={u.isActive ? 'success' : 'default'}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="text-neutral-400">{fmt(u.createdAt)}</td>
                  <td>
                    <button
                      onClick={() => openManage(u)}
                      className="flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
                    >
                      <ShieldPlus size={13} /> Manage roles
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddUser && (
        <Modal title="Add user" onClose={() => setShowAddUser(false)} size="sm">
          <div className="space-y-4">
            <Field label="Full name">
              <input
                type="text"
                value={newUser.fullName}
                onChange={(e) => setNewUser((p) => ({ ...p, fullName: e.target.value }))}
                placeholder="Jane Smith"
                className="field"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                placeholder="jane@company.com"
                className="field"
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={newUser.phone}
                onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+1 555 000 0000"
                className="field"
              />
            </Field>
            {addError && <p className="text-xs text-red-600">{addError}</p>}
            <p className="text-xs text-neutral-400">A welcome email with the temporary password will be sent to the user.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowAddUser(false)}>
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={!newUser.fullName || !newUser.email || !newUser.phone || add.isPending}>
                {add.isPending ? 'Creating' : 'Create user'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {manageTarget && (
        <Modal title={`Manage roles — ${manageTarget.fullName}`} onClose={() => setManageTarget(null)} size="sm">
          <div className="space-y-4">
            {roles.length === 0 ? (
              <p className="text-xs text-neutral-400">No roles available in this organisation.</p>
            ) : (
              <div className="space-y-2">
                {roles.map((r: Role) => (
                  <label key={r.id} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={checkedRoles.has(r.id)}
                      onChange={() => toggleRole(r.id)}
                      className="mt-0.5 accent-indigo-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-neutral-800">{r.name}</div>
                      {r.description && <div className="text-xs text-neutral-400 mt-0.5">{r.description}</div>}
                    </div>
                  </label>
                ))}
              </div>
            )}
            {manageError && <p className="text-xs text-red-600">{manageError}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setManageTarget(null)}>Cancel</Button>
              <Button onClick={handleSaveRoles} disabled={isSavingRoles}>
                {isSavingRoles ? 'Saving' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
